import { Button, CloseButton, Group, Modal, Radio, Stack, Text } from "@mantine/core";
import { useState } from "react";

export interface UseStyleOrImageProps {
  opened: boolean;
  /** Cancel, the x, or "Keep Style Configuration": the images are dropped. */
  onKeepStyle: () => void;
  /** "Use Image": the Style and its copies are cleared, then the images are taken. */
  onUseImage: () => void;
}

type Choice = "keep" | "image";

/**
 * Live's "Use Style or Image?" (`style-or-image-selection-modal`, capture advanced-234/26): asked
 * when references arrive while the Style has ticked copies. "Keep Style Configuration" is chosen
 * at first.
 */
export function UseStyleOrImage({ opened, onKeepStyle, onUseImage }: UseStyleOrImageProps) {
  return (
    <Modal.Root opened={opened} onClose={onKeepStyle} centered size="auto">
      <Modal.Overlay />
      <Modal.Content aria-label="Use Style or Image?">
        <Modal.Body>
          {/* Mounted while open: each asking starts on "Keep Style Configuration". */}
          <Choose onKeepStyle={onKeepStyle} onUseImage={onUseImage} />
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}

function Choose({ onKeepStyle, onUseImage }: Omit<UseStyleOrImageProps, "opened">) {
  const [choice, setChoice] = useState<Choice>("keep");
  return (
    <Stack gap={20} maw={760}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div>
          <Text fw={600} size="lg">
            Use Style or Image?
          </Text>
          <Text size="sm" c="var(--cai-color-text-muted)">
            You already set up Ad Copy based on a style. How would you like to proceed?
          </Text>
        </div>
        <CloseButton aria-label="Close" onClick={onKeepStyle} />
      </Group>
      <Radio.Group
        value={choice}
        onChange={(value) => setChoice(value === "image" ? "image" : "keep")}
      >
        <Group grow align="stretch" wrap="nowrap">
          <Radio.Card value="keep" p="md" radius="md">
            <Group wrap="nowrap" align="flex-start">
              <Radio.Indicator />
              <div>
                <Text fw={600}>Keep Style Configuration</Text>
                <Text size="sm" c="var(--cai-color-text-muted)">
                  Continue with your existing style-based settings. The inspiration image will not
                  be included in the generation.
                </Text>
              </div>
            </Group>
          </Radio.Card>
          <Radio.Card value="image" p="md" radius="md">
            <Group wrap="nowrap" align="flex-start">
              <Radio.Indicator />
              <div>
                <Text fw={600}>Use Image</Text>
                <Text size="sm" c="var(--cai-color-text-muted)">
                  Clear your current style configuration and set up new Ad Copy for the inspiration
                  image.
                </Text>
              </div>
            </Group>
          </Radio.Card>
        </Group>
      </Radio.Group>
      <Group justify="flex-end" gap={12}>
        <Button variant="outline" onClick={onKeepStyle}>
          Cancel
        </Button>
        <Button onClick={choice === "image" ? onUseImage : onKeepStyle}>Continue</Button>
      </Group>
    </Stack>
  );
}
